import * as store from '@attain-apps/fe/web/main/lib/store'
import { TypeChecker } from '@attain-apps/type-guards'
import * as rxjs from 'rxjs'

import { SPACE_RECORDS_SELECTION_TARGET } from '../../../constants'
import type { Plugin } from '../plugin'

const INITIATE_CANCELLATION_REASON = {
  mixedOccupiers:
    'Cannot assign clusters for Space records with different occupiers.',
  noSelectedRecords: 'No Space records selected for cluster assignment.',
  recordsNotAvailable: (recordIds: readonly string[]) =>
    `Space records '${recordIds.join(', ')}' are not available.`,
} as const

/**
 * Resolves selected records used by the assignment workflow.
 */
export const initiate = store.effects.create(
  (feature: Plugin['feature'], plugin: Plugin) =>
    feature.actions.initiate.relay((forwardTo, { recordIds }) => {
      const selectedRecordIds$ = TypeChecker.isUndefined(recordIds)
        ? plugin.collection.plugins.selectionPlugin.feature.selectors.getAllSelectedIds(
            {
              target: SPACE_RECORDS_SELECTION_TARGET,
            },
          ).asObservable
        : rxjs.of(recordIds)

      return selectedRecordIds$.pipe(
        rxjs.take(1),
        rxjs.switchMap(selectedRecordIds => {
          if (selectedRecordIds.length === 0) {
            return rxjs.of({
              cancellation: {
                reason: INITIATE_CANCELLATION_REASON.noSelectedRecords,
              },
            })
          }
          // FIXME: Record availability should be guaranteed by the cache/loading
          // boundary before this workflow starts, not validated in this effect.
          return plugin.collection.plugins.cachePlugin.feature.selectors
            .getCachedEntities(selectedRecordIds)
            .asObservable.pipe(
              rxjs.take(1),
              rxjs.map(cachedRecords => {
                const records = cachedRecords.filter(
                  TypeChecker.isNotNullish.raw,
                )

                if (records.length !== selectedRecordIds.length) {
                  return {
                    cancellation: {
                      reason:
                        INITIATE_CANCELLATION_REASON.recordsNotAvailable(
                          selectedRecordIds,
                        ),
                    },
                  }
                }

                const occupierIds = new Set(
                  records.map(record => record.occupierId),
                )
                // Cluster options are filtered by occupier, so one modal cannot
                // safely edit records from different occupiers.
                if (occupierIds.size > 1) {
                  return {
                    cancellation: {
                      reason: INITIATE_CANCELLATION_REASON.mixedOccupiers,
                    },
                  }
                }

                if (TypeChecker.isUndefined(recordIds)) {
                  return {
                    success: true,
                  }
                }

                // Explicit ids, like the record-details current record, are
                // normalized into the fixed record-selection target before the
                // workflow opens.
                return forwardTo(
                  plugin.collection.plugins.selectionPlugin.applied.actions
                    .putInSelectionLookup,
                  {
                    input: {
                      key: SPACE_RECORDS_SELECTION_TARGET,
                      value: { set: selectedRecordIds },
                    },
                    output: () => undefined,
                  },
                )
              }),
            )
        }),
      )
    }).asObservable,
)
